import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { API_URLS } from '@core/api-url.enum';
import { ApiCacheService, CacheType, CacheTypes } from '@core/cache/api-cache.service';
import { ApiCallService } from '@services/api-call.service';
import {
  SUB_PAGE_API,
  getSubPageSlug
} from '@modules/whitelabel-cover-page/sub-pages/sub-pages.constants';
import {
  SubPageApiData,
  SubPageApiResponse,
  SubPagesListApiResponse
} from '@modules/whitelabel-cover-page/sub-pages/sub-pages.interface';
import {
  MarketingPageSectionWithData
} from '@modules/whitelabel-cover-page/marketing-page-section/marketing-page-section.interface';
import { WhitelabelCoverPageApiSection } from '@modules/whitelabel-cover-page/whitelabel-cover-page.interface';
import { findSubPageByRoute } from '@modules/sub-page-view/sub-page-view.utils';

const FIELDS = SUB_PAGE_API.FIELDS;
const QUERY = SUB_PAGE_API.QUERY;

@Injectable({
  providedIn: 'root'
})
export class SubPagesApiService {
  constructor(
    private readonly apiCacheService: ApiCacheService,
    private readonly apiCallService: ApiCallService
  ) {}

  /**
   * Owner list: GET /rest/sub-page (auth token identifies creator).
   * Public list: GET /rest/sub-page?user_id=<uuid>
   */
  getSubPages(
    userId?: string,
    cacheType: CacheType = CacheTypes.ALWAYS_FRESH
  ): Observable<SubPagesListApiResponse> {
    const endpoint = userId
      ? `${API_URLS.subPage}?${QUERY.USER_ID}=${encodeURIComponent(userId)}`
      : API_URLS.subPage;
    return this.apiCacheService.get(endpoint, cacheType);
  }

  getPublicSubPages(userId: string): Observable<SubPagesListApiResponse> {
    return this.getSubPages(userId, CacheTypes.REFRESH_EVERY_N);
  }

  getSubPageById(
    pageId: number,
    userId?: string
  ): Observable<SubPageApiResponse> {
    const params = [`${QUERY.PAGE_ID}=${pageId}`];
    if (userId) {
      params.push(`${QUERY.USER_ID}=${encodeURIComponent(userId)}`);
    }
    return this.apiCacheService.get(
      `${API_URLS.subPage}?${params.join('&')}`,
      CacheTypes.ALWAYS_FRESH
    );
  }

  getSubPageByUrl(
    url: string,
    userId: string,
    preview = false
  ): Observable<SubPageApiResponse> {
    const params = [
      `${QUERY.USER_ID}=${encodeURIComponent(userId)}`,
      `${QUERY.URL}=${encodeURIComponent(url)}`
    ];
    if (preview) {
      params.push(`${QUERY.PREVIEW}=true`);
    }
    return this.apiCacheService.get(
      `${API_URLS.subPage}?${params.join('&')}`,
      CacheTypes.ALWAYS_FRESH
    );
  }

  getPublicSubPageByRoute(
    username: string,
    slug: string,
    userId: string,
    preview = false
  ): Observable<SubPageApiResponse> {
    const pageSlug = getSubPageSlug(slug, username);
    const normalizedUsername = (username || '').trim().replace(/^\/+|\/+$/g, '');
    return this.findPageInList(normalizedUsername, pageSlug, userId, preview);
  }

  private findPageInList(
    username: string,
    pageSlug: string,
    userId: string,
    preview: boolean
  ): Observable<SubPageApiResponse> {
    return this.getPublicSubPages(userId).pipe(
      switchMap((response) => {
        const publicMatch = this.toMatchedResponse(response, username, pageSlug);
        if (publicMatch || !preview) {
          return of(
            publicMatch ?? {
              status: false,
              statusCode: 404,
              message: '',
              data: {} as SubPageApiData
            }
          );
        }
        return this.getSubPages().pipe(
          map(
            (ownerResponse) =>
              this.toMatchedResponse(ownerResponse, username, pageSlug) ?? {
                status: false,
                statusCode: 404,
                message: '',
                data: {} as SubPageApiData
              }
          ),
          catchError(() =>
            of({
              status: false,
              statusCode: 404,
              message: '',
              data: {} as SubPageApiData
            })
          )
        );
      }),
      catchError(() =>
        of({
          status: false,
          statusCode: 404,
          message: '',
          data: {} as SubPageApiData
        })
      )
    );
  }

  private toMatchedResponse(
    response: SubPagesListApiResponse | null | undefined,
    username: string,
    pageSlug: string
  ): SubPageApiResponse | null {
    const match = findSubPageByRoute(response?.data, username, pageSlug);
    if (!match) {
      return null;
    }
    return {
      status: true,
      statusCode: response?.statusCode ?? 200,
      message: response?.message ?? '',
      data: match
    };
  }

  createSubPage(
    payload: Record<string, unknown>
  ): Observable<SubPageApiResponse> {
    return this.put<SubPageApiData>({
      ...payload,
      [FIELDS.ACTION]: SUB_PAGE_API.ACTIONS.CREATE_PAGE
    });
  }

  updateSubPage(
    pageId: number,
    payload: Record<string, unknown>
  ): Observable<SubPageApiResponse> {
    return this.put<SubPageApiData>({
      ...payload,
      [FIELDS.ACTION]: SUB_PAGE_API.ACTIONS.UPDATE_PAGE,
      [FIELDS.PAGE_ID]: pageId
    });
  }

  deleteSubPage(
    pageId: number
  ): Observable<SubPageApiResponse<Record<string, never>>> {
    return this.put<Record<string, never>>({
      [FIELDS.ACTION]: SUB_PAGE_API.ACTIONS.DELETE_PAGE,
      [FIELDS.PAGE_ID]: pageId
    });
  }

  createSubPageSection(
    pageId: number,
    item: MarketingPageSectionWithData
  ): Observable<SubPageApiResponse<WhitelabelCoverPageApiSection>> {
    return this.put<WhitelabelCoverPageApiSection>({
      ...this.buildSectionPayload(item),
      [FIELDS.ACTION]: SUB_PAGE_API.ACTIONS.CREATE_SECTION,
      [FIELDS.PAGE_ID]: pageId
    });
  }

  updateSubPageSection(
    pageId: number,
    sectionId: number,
    item: MarketingPageSectionWithData
  ): Observable<SubPageApiResponse<WhitelabelCoverPageApiSection>> {
    return this.put<WhitelabelCoverPageApiSection>({
      ...this.buildSectionPayload(item),
      [FIELDS.ACTION]: SUB_PAGE_API.ACTIONS.UPDATE_SECTION,
      [FIELDS.PAGE_ID]: pageId,
      [FIELDS.SECTION_ID]: sectionId
    });
  }

  deleteSubPageSection(
    pageId: number,
    sectionId: number
  ): Observable<SubPageApiResponse<Record<string, never>>> {
    return this.put<Record<string, never>>({
      [FIELDS.ACTION]: SUB_PAGE_API.ACTIONS.DELETE_SECTION,
      [FIELDS.PAGE_ID]: pageId,
      [FIELDS.SECTION_ID]: sectionId
    });
  }

  reorderSubPageSections(
    pageId: number,
    sections: Array<{ section_id: number; sequence_number: number }>
  ): Observable<SubPageApiResponse<{ success?: boolean }>> {
    return this.put<{ success?: boolean }>({
      [FIELDS.ACTION]: SUB_PAGE_API.ACTIONS.REORDER_SECTIONS,
      [FIELDS.PAGE_ID]: pageId,
      [FIELDS.SECTIONS]: sections
    });
  }

  private put<T>(
    payload: Record<string, unknown>
  ): Observable<SubPageApiResponse<T>> {
    return this.apiCallService.put(
      API_URLS.subPage,
      payload,
      true
    ) as Observable<SubPageApiResponse<T>>;
  }

  private buildSectionPayload(
    item: MarketingPageSectionWithData
  ): Record<string, unknown> {
    const layoutId = item.section.layoutId;
    return {
      ...item.data,
      [FIELDS.LAYOUT_ID]: layoutId,
      [FIELDS.SECTION_TYPE]: layoutId,
      [FIELDS.TITLE]: item.section.name,
      [FIELDS.SEQUENCE_NUMBER]: item.section.sequenceOrder ?? 1,
      [FIELDS.IS_VISIBLE]: item.section.isVisible
    };
  }
}
